import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Cardiac subsection adapter
 * Handles cardiovascular assessment and heart sounds (4 fields)
 */
export class CardiacAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cardiac assessment fields
    const cardiacStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_WNL
    )?.at(0)

    const cardiacAbnormalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_FACTORS
    )

    const heartSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_HEART_SOUNDS
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.CARDIAC_STATUS]: cardiacStatus,
      [SocFormField.CARDIAC_ABNORMAL_FACTORS]: cardiacAbnormalFactors,
      [SocFormField.CARDIAC_HEART_SOUNDS]: heartSounds,
      [SocFormField.CARDIAC_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_WNL,
        value: this.getFormDataField(SocFormField.CARDIAC_STATUS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_HEART_SOUNDS,
        value: this.getFormDataField(SocFormField.CARDIAC_HEART_SOUNDS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.CARDIAC_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add cardiovascular factors if they exist (they are conditionally rendered when status === 'Abnormal')
    if (this.formDataFieldExists(SocFormField.CARDIAC_ABNORMAL_FACTORS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARDIAC_CARDIOVASCULAR_FACTORS,
        value: this.getFormDataField(SocFormField.CARDIAC_ABNORMAL_FACTORS)
      })
    }

    return mutations
  }
}
